<template>
  <!-- 工位质检执行：当前为交互原型，提交和附件尚未接入后端。 -->
  <MainPage>
    <div class="quality-page">

      <header class="page-header">
        <div>
          <h1>质检执行 · 工位端</h1>
          <small>包装 MES</small>
        </div>
        <span class="status" :class="statusClass">{{ inspectionStatus }}</span>
        <div class="Back">
          <ScanCodeDialog></ScanCodeDialog>
          <BackButton></BackButton>
        </div>
      </header>

      <main class="page-main">
        <InspectionTasks />

        <div class="inspection-content">
          <section class="card">
            <div class="order-head">
              <div>
                <h2>{{ getWorkOrder.orderCode || '-' }} · {{ getWorkOrder.orderName || '-' }}</h2>
                <span class="muted">{{ selectedPatrolTask ? `巡检任务 ${selectedPatrolTask.code}` : '请选择巡检任务' }}</span>
              </div>
              <div class="actions">
                <ProductImageButton :order-code="getWorkOrder.orderCode" />
                <ComplaintCaseButton />
              </div>
            </div>
            <div class="meta">
              <span>客户：<b>{{ getWorkOrder.customer || '-' }}</b></span>
              <span v-if="selectedPatrolTask">当前工序：<b>{{ selectedPatrolTask.processName || '-' }}</b></span>
              <span>订单量：<b>{{ getWorkOrder.orderQuantity || '-' }}</b></span>
            </div>
          </section>

          <template v-if="hasInspection">
            <div class="notice">
              逐项点选“合格 /
              不合格”，无需填写实测结果。前工序项目须在本次重新判断。
            </div>

            <section v-for="stageIndex in visibleStageIndexes" :key="stages[stageIndex].name" class="group"
              :class="{ inherited: stageIndex !== activeStageIndex }">
              <h3>
                <span>{{
                  stageIndex === activeStageIndex
                    ? "当前工序 · "
                    : "前工序复检 · "
                }}{{ stages[stageIndex].name }}</span><span>{{ stages[stageIndex].items.length }} 项必检</span>
              </h3>
              <div v-for="(item, itemIndex) in stages[stageIndex].items" :id="itemId(stageIndex, itemIndex)"
                :key="item.name" class="item" :class="{
                  missing: isMissing(stageIndex, itemIndex),
                  failed: answerOf(stageIndex, itemIndex) === 'fail',
                }">
                <span class="number">{{ itemIndex + 1 }}</span>
                <div>
                  <strong>{{ item.name }}</strong>
                  <p>标准：{{ item.standard }}</p>
                </div>
                <div class="choices">
                  <AButton :class="{ chosen: answerOf(stageIndex, itemIndex) === 'pass' }"
                    :disabled="currentInspection.submitted || actionLoading"
                    @click="choose(stageIndex, itemIndex, 'pass')">✓ 合格</AButton>
                  <AButton class="fail-button" :class="{ chosen: answerOf(stageIndex, itemIndex) === 'fail' }"
                    :disabled="currentInspection.submitted || actionLoading"
                    @click="choose(stageIndex, itemIndex, 'fail')">× 不合格</AButton>
                </div>
              </div>
            </section>

            <InspectionRemarkAttachment :key="selectedPatrolTask.id" :inspection="currentInspection"
              :photo-list="photoList" :disabled="currentInspection.submitted || actionLoading" />
          </template>
          <div v-else class="empty">请选择左侧巡检任务，加载检验项目。</div>
        </div>
      </main>

      <InspectionFooter v-if="hasInspection" ref="inspectionFooter" :summary="summary"
        :progress-percent="progressPercent" :submitted="currentInspection.submitted"
        :task-id="selectedPatrolTask && selectedPatrolTask.id" :results="submitResults"
        :remark="currentInspection.remark" :evidence="currentInspection.evidence" @request-submit="prepareSubmit"
        @submitted="handleSubmitSuccess" @revoked="handleRevokeSuccess" @loading-change="actionLoading = $event" />
    </div>
  </MainPage>
</template>

<script>
import { mapGetters } from 'vuex'
import { inspectionPatrolTaskId } from '@api'
import BackButton from '@c/stationWork/btnList/common/BackButton.vue'
import ScanCodeDialog from './ScanCodeDialog.vue'
import ComplaintCaseButton from "./btns/ComplaintCaseButton.vue";
import InspectionFooter from "./InspectionFooter.vue";
import InspectionRemarkAttachment from "./InspectionRemarkAttachment.vue";
import InspectionTasks from "./InspectionTasks.vue";
import ProductImageButton from "./btns/ProductImageButton.vue";
export default {
  components: {
    BackButton,
    ScanCodeDialog,
    ComplaintCaseButton,
    InspectionFooter,
    InspectionRemarkAttachment,
    InspectionTasks,
    ProductImageButton,
  },
  data() {
    return {
      stages: [],
      activeStageIndex: 0,
      inspectionTaskRequestId: 0,
      actionLoading: false,
      inspections: [],
      missingKeys: [],
      photoList: [],
    };
  },
  computed: {
    ...mapGetters(["getWorkOrder"]),
    // 当前选中任务统一从 Vuex 读取，避免页面与其他按钮组件各自保存选中状态。
    selectedPatrolTask() {
      return this.getWorkOrder && this.getWorkOrder.id ? this.getWorkOrder : null;
    },
    activeStage() {
      return this.stages[this.activeStageIndex] || null;
    },
    currentInspection() {
      return this.inspections[this.activeStageIndex] || {
        answers: {},
        remark: "",
        evidence: [],
        submitted: false,
        status: "待检验",
      };
    },
    hasInspection() {
      return Boolean(this.activeStage && this.inspections[this.activeStageIndex]);
    },
    visibleStageIndexes() {
      if (!this.hasInspection) return [];
      return Array.from(
        { length: this.activeStageIndex + 1 },
        (_, index) => this.activeStageIndex - index,
      );
    },
    inspectedItems() {
      return this.stages
        .slice(0, this.activeStageIndex + 1)
        .reduce(
          (all, stage, stageIndex) =>
            all.concat(
              stage.items.map((item, itemIndex) => ({
                stageIndex,
                itemIndex,
                item,
              })),
            ),
          [],
        );
    },
    summary() {
      const answers = this.currentInspection.answers;
      const done = this.inspectedItems.filter(
        ({ stageIndex, itemIndex }) =>
          answers[this.answerKey(stageIndex, itemIndex)],
      ).length;
      const fail = this.inspectedItems.filter(
        ({ stageIndex, itemIndex }) =>
          answers[this.answerKey(stageIndex, itemIndex)] === "fail",
      ).length;
      return { total: this.inspectedItems.length, done, fail };
    },
    progressPercent() {
      return this.summary.total
        ? Math.round((this.summary.done / this.summary.total) * 100)
        : 0;
    },
    inspectionStatus() {
      return this.currentInspection.submitted
        ? this.currentInspection.status
        : this.summary.done
          ? "检验中"
          : "待检验";
    },
    statusClass() {
      if (!this.currentInspection.submitted) return "";
      return this.currentInspection.status === "不合格" ? "fail" : "pass";
    },
    submitResults() {
      return this.inspectedItems
        .filter(({ stageIndex, itemIndex }) => this.answerOf(stageIndex, itemIndex))
        .map(({ stageIndex, itemIndex, item }) => ({
          itemId: item.id,
          qualified: this.answerOf(stageIndex, itemIndex) === "pass",
        }))
        .filter((item) => item.itemId);
    },
  },
  methods: {
    async loadSelectedPatrolTask(task) {
      const requestId = ++this.inspectionTaskRequestId;
      if (!task) {
        this.stages = [];
        this.inspections = [];
        this.photoList = [];
        this.activeStageIndex = 0;
        this.missingKeys = [];
        return;
      }

      const stageIndex = this.stages.findIndex((stage) => stage.name === task.processName);
      if (stageIndex >= 0) {
        this.activeStageIndex = stageIndex;
      }

      try {
        const response = await inspectionPatrolTaskId(task.id);
        if (requestId !== this.inspectionTaskRequestId) return;

        const detail = response.data || response;
        this.applyPatrolTaskDetail(detail);
        console.log("inspectionPatrolTaskId", response);
      } catch (error) {
        if (requestId === this.inspectionTaskRequestId) {
          this.$message.error("巡检任务详情加载失败，请稍后重试。");
        }
      }
    },
    // 详情接口按检验项返回数据，转换为页面所需的“工序 - 项目”分组结构。
    applyPatrolTaskDetail(detail) {
      const processItems = Array.isArray(detail.processItemList)
        ? detail.processItemList.slice().sort((first, second) => first.seq - second.seq)
        : [];
      if (!processItems.length) {
        this.stages = [];
        this.inspections = [];
        this.photoList = [];
        this.activeStageIndex = 0;
        return;
      }

      const stages = processItems.reduce((groups, item) => {
        let stage = groups.find((group) => group.processId === item.processId);
        if (!stage) {
          stage = {
            processId: item.processId,
            name: item.processName || "未设置工序",
            task: detail.code,
            items: [],
          };
          groups.push(stage);
        }
        stage.items.push({
          id: item.id,
          name: item.itemName || "未命名检验项",
          standard: item.standardValue || "—",
          qualified: item.qualified,
        });
        return groups;
      }, []);
      const currentStageIndex = stages.findIndex((stage) => stage.processId === detail.processId);
      const activeStageIndex = currentStageIndex >= 0 ? currentStageIndex : stages.length - 1;
      const answers = {};

      stages.forEach((stage, stageIndex) => {
        stage.items.forEach((item, itemIndex) => {
          if (typeof item.qualified === "boolean") {
            answers[this.answerKey(stageIndex, itemIndex)] = item.qualified ? "pass" : "fail";
          }
        });
      });

      this.stages = stages;
      this.activeStageIndex = activeStageIndex;
      this.photoList = Array.isArray(detail.photoList) ? detail.photoList : [];
      this.inspections = stages.map((stage, stageIndex) => ({
        answers: stageIndex === activeStageIndex ? answers : {},
        remark: "",
        evidence: [],
        submitted: detail.status && detail.status.code === 1,
        status: (detail.status && detail.status.name) || "待检验",
      }));
      this.missingKeys = [];
    },
    answerKey(stageIndex, itemIndex) {
      return `${stageIndex}-${itemIndex}`;
    },
    itemId(stageIndex, itemIndex) {
      return `inspection-item-${this.answerKey(stageIndex, itemIndex)}`;
    },
    answerOf(stageIndex, itemIndex) {
      return this.currentInspection.answers[
        this.answerKey(stageIndex, itemIndex)
      ];
    },
    isMissing(stageIndex, itemIndex) {
      return this.missingKeys.includes(this.answerKey(stageIndex, itemIndex));
    },
    choose(stageIndex, itemIndex, value) {
      if (this.currentInspection.submitted) return;
      const key = this.answerKey(stageIndex, itemIndex);
      this.$set(this.currentInspection.answers, key, value);
      this.missingKeys = this.missingKeys.filter((item) => item !== key);
    },
    prepareSubmit() {
      if (!this.selectedPatrolTask) {
        this.$message.warning("请先从左侧选择巡检任务。");
        return;
      }

      const missing = this.inspectedItems.filter(
        ({ stageIndex, itemIndex }) => !this.answerOf(stageIndex, itemIndex),
      );
      if (!missing.length) {
        this.$refs.inspectionFooter.openSubmitModal();
        return;
      }
      this.missingKeys = missing.map(({ stageIndex, itemIndex }) =>
        this.answerKey(stageIndex, itemIndex),
      );
      this.$nextTick(() => {
        const first = missing[0];
        const target = document.getElementById(
          this.itemId(first.stageIndex, first.itemIndex),
        );
        if (target)
          target.scrollIntoView({ behavior: "smooth", block: "center" });
      });
      this.$message.warning(`还有 ${missing.length} 项未判断，请逐项选择。`);
    },
    // 底部操作组件完成接口调用后，仅由页面更新检验状态。
    handleSubmitSuccess() {
      this.currentInspection.submitted = true;
      this.currentInspection.status = this.summary.fail ? "不合格" : "检验完成";
    },
    handleRevokeSuccess() {
      this.currentInspection.submitted = false;
      this.currentInspection.status = "待检验";
    },
  },
  watch: {
    getWorkOrder: {
      immediate: true,
      handler(task) {
        this.loadSelectedPatrolTask(task && task.id ? task : null);
      },
    },
    activeStageIndex() {
      this.missingKeys = [];
    },
  },
};
</script>

<style lang="less" scoped>
.quality-page {
  display: flex;
  flex-direction: column;
  height: 100vh;
  min-height: 0;
  overflow: hidden;
  color: #192d45;
  background: #edf2f7;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 15px 24px;
  color: #fff;
  background: #123456;
}

.page-header h1 {
  margin: 0;
  color: #fff;
  font-size: 22px;
}

.page-header small {
  color: #c1d4e8;
  font-size: 13px;
}

.page-main {
  flex: 1;
  display: grid;
  grid-template-columns: 360px minmax(0, 1fr);
  height: 0;
  min-height: 0;
  overflow: hidden;
  align-items: stretch;
}

.inspection-content {
  box-sizing: border-box;
  width: 100%;
  min-height: 0;
  padding: 20px 24px 110px;
  overflow-y: auto;
}

.card {
  padding: 18px;
  margin-bottom: 16px;
  background: #fff;
  border: 1px solid #dce4ec;
  border-radius: 14px;
}

.order-head,
.actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.order-head,
.actions {
  flex-wrap: wrap;
}

.order-head> :first-child {
  min-width: 0;
}

.order-head h2 {
  margin: 0 0 5px;
  font-size: 21px;
  overflow-wrap: anywhere;
}

.actions .ant-btn {
  color: #1566bb;
  border-color: #87b3e4;
  background: #f4f9ff;
}

.actions .ant-btn-primary {
  color: #fff;
  background: #176ed0;
  border-color: #176ed0;
}

// 产品图片和客户投诉案例入口统一尺寸，避免中文长度不同造成按钮不齐。
.order-head .actions /deep/ .ant-btn {
  box-sizing: border-box;
  width: 100%;
  height: 40px;
  padding: 0;
}

// 两个入口的组件根节点也占据固定列，避免平板上按钮发生重叠。
.order-head .actions {
  display: grid;
  grid-template-columns: repeat(2, 116px);
  flex: 0 0 auto;
  gap: 12px;
}

.order-head .actions>* {
  width: 116px;
  min-width: 0;
}

.muted {
  color: #62758a;
  font-size: 14px;
}

.meta,
.spec {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 24px;
  margin-top: 12px;
  font-size: 14px;
}

.card details {
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid #e5ebf1;
}

.card summary {
  cursor: pointer;
  font-weight: 600;
}

.spec {
  color: #536b83;
}

.notice {
  padding: 11px 15px;
  margin-bottom: 18px;
  color: #81521b;
  font-size: 14px;
  background: #fff6e6;
  border-left: 4px solid #dd9b32;
  border-radius: 7px;
}

.status {
  padding: 5px 10px;
  margin-left: auto;
  color: #1767b9;
  font-size: 14px;
  font-weight: 600;
  background: #eaf3fd;
  border-radius: 7px;
}

.status.pass {
  color: #137c51;
  background: #dff5eb;
}

.status.fail {
  color: #b02e28;
  background: #ffe7e4;
}

.group {
  overflow: hidden;
  margin-bottom: 16px;
  background: #fff;
  border: 1px solid #dce4ec;
  border-radius: 13px;
}

.group h3 {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 18px;
  margin: 0;
  font-size: 17px;
  background: #eff6ff;
  border-bottom: 1px solid #dce4ec;
}

.group h3 span:last-child {
  color: #617b95;
  font-size: 14px;
  font-weight: 400;
}

.group.inherited h3 {
  background: #fff7eb;
}

.item {
  display: grid;
  grid-template-columns: 38px minmax(0, 1fr) 240px;
  gap: 12px;
  align-items: center;
  padding: 17px 18px;
  border-bottom: 1px solid #e6edf3;
  scroll-margin: 24px;
}

.item:last-child {
  border-bottom: 0;
}

.item.missing {
  background: #fff0ed;
  box-shadow: inset 4px 0 #dc4a39;
}

.item.failed {
  background: #fffaf8;
}

.number {
  width: 32px;
  height: 32px;
  color: #627a94;
  line-height: 32px;
  text-align: center;
  background: #eef3f8;
  border-radius: 8px;
}

.item strong {
  display: block;
  font-size: 17px;
}

.item p {
  margin: 4px 0 0;
  color: #60748a;
  font-size: 15px;
}

.choices {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.choices .ant-btn {
  height: 58px;
  font-size: 17px;
  border-width: 2px;
}

.choices .chosen {
  color: #087345;
  background: #e0f5e9;
  border-color: #21a36a;
}

.choices .fail-button.chosen {
  color: #b02118;
  background: #ffe7e3;
  border-color: #da594d;
}

.empty {
  padding: 45px 24px;
  text-align: center;
  background: #f3f6fa;
  border: 1px dashed #bacbdb;
  border-radius: 12px;
}

@media (max-width: 900px) {
  .page-header {
    padding: 14px 16px;
  }

  .page-main {
    grid-template-columns: 1fr;
    overflow-y: auto;
  }

  .inspection-content {
    height: auto;
    padding: 14px;
    overflow: visible;
  }

  .order-head .actions {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    width: 100%;
  }

  .order-head .actions>* {
    width: auto;
  }

  .item {
    grid-template-columns: 30px minmax(0, 1fr);
    padding: 16px;
  }

  .choices {
    grid-column: 2;
  }

  .order-head .actions {
    width: 100%;
  }

  .order-head .actions .ant-btn {
    flex: 1;
  }

}

.Back {
  display: flex;
  gap: 10px;
  align-items: center;
}
</style>
